'use client'

import { cn } from '@/lib/utils'
import { groupCriteriaResults } from '@/components/evaluation/evaluation-criteria-section'
import type {
  EvaluationAnalysisPayload,
  EvaluationCriterionResult,
} from '@/hooks/use-evaluation-analysis'

export type RequirementTier = 'required' | 'preferred'

export type RequirementCoverageRow = EvaluationCriterionResult & {
  tier: RequirementTier
}

export type RequirementsCoverageSectionProps = {
  /** Requirement rows (optionally with `tier`; inferred when omitted). */
  requirements: EvaluationCriterionResult[] | RequirementCoverageRow[]
  className?: string
  /** Optional override for the helper line under the title. */
  description?: string
}

function requirementMet(criterion: EvaluationCriterionResult): boolean | undefined {
  if (typeof criterion.met === 'boolean') return criterion.met
  if (typeof criterion.score === 'number') return criterion.score >= 0.5
  return undefined
}

export function summarizeRequirements(
  requirements: EvaluationCriterionResult[],
): { metCount: number; knownCount: number } {
  const statuses = requirements.map(requirementMet)
  const known = statuses.filter((s) => s !== undefined)
  const metCount = statuses.filter((s) => s === true).length
  return { metCount, knownCount: known.length }
}

function slugRequirementKey(prefix: string, label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return `${prefix}_${slug || 'item'}`
}

export function cleanRequirementLabel(label: string): string {
  return label
    .replace(/\s*·\s*preferred$/i, '')
    .replace(/\s*\(\s*preferred\s*\)$/i, '')
    .trim()
}

export function inferRequirementTier(criterion: EvaluationCriterionResult): RequirementTier {
  if ('tier' in criterion && (criterion.tier === 'required' || criterion.tier === 'preferred')) {
    return criterion.tier
  }
  if (criterion.key.startsWith('pref_')) return 'preferred'
  if (/\s*·\s*preferred$/i.test(criterion.label)) return 'preferred'
  if (/\(\s*preferred\s*\)$/i.test(criterion.label)) return 'preferred'
  return 'required'
}

export function toRequirementCoverageRow(
  criterion: EvaluationCriterionResult | RequirementCoverageRow,
): RequirementCoverageRow {
  const tier = inferRequirementTier(criterion)
  return {
    ...criterion,
    label: cleanRequirementLabel(criterion.label),
    tier,
  }
}

function collectCandidateSkillNames(payload: EvaluationAnalysisPayload): Set<string> {
  const names = new Set<string>()
  for (const domain of payload.candidate_skills_by_domain ?? []) {
    for (const skill of domain.skills) {
      names.add(skill.canonical.trim().toLowerCase())
      names.add(skill.display.trim().toLowerCase())
    }
  }
  for (const skill of payload.unmapped_skills ?? []) {
    names.add(skill.display.trim().toLowerCase())
  }
  return names
}

function requirementMetFromProfileSkills(
  candidateSkills: Set<string>,
  requirement: string,
): boolean | undefined {
  const fold = requirement.trim().toLowerCase()
  if (!fold) return undefined
  if (candidateSkills.size === 0) return undefined
  return candidateSkills.has(fold)
}

function legacyRequirementsFromPayload(
  payload: EvaluationAnalysisPayload,
): RequirementCoverageRow[] {
  const required = payload.requirements?.required_skills ?? []
  const preferred = payload.requirements?.preferred_skills ?? []
  if (required.length === 0 && preferred.length === 0) return []

  const candidateSkills = collectCandidateSkillNames(payload)
  const rows: RequirementCoverageRow[] = []

  for (const skill of required) {
    rows.push({
      key: slugRequirementKey('req', skill),
      kind: 'requirement',
      label: skill,
      tier: 'required',
      met: requirementMetFromProfileSkills(candidateSkills, skill),
    })
  }
  for (const skill of preferred) {
    rows.push({
      key: slugRequirementKey('pref', skill),
      kind: 'requirement',
      label: skill,
      tier: 'preferred',
      met: requirementMetFromProfileSkills(candidateSkills, skill),
    })
  }

  return rows
}

function catalogSkillNames(domains: EvaluationAnalysisPayload['candidate_skills_by_domain']): Set<string> {
  const names = new Set<string>()
  for (const domain of domains ?? []) {
    for (const skill of domain.skills) {
      names.add(skill.canonical.trim().toLowerCase())
      names.add(skill.display.trim().toLowerCase())
    }
  }
  return names
}

/**
 * Profile skills outside the rubric catalog that match a met job requirement —
 * for the Analysis "Other" row (not every unmapped profile skill).
 */
export function matchingUnmappedSkillsForOther(
  payload: EvaluationAnalysisPayload,
  requirements: EvaluationCriterionResult[],
): NonNullable<EvaluationAnalysisPayload['unmapped_skills']> {
  const metRequirementNames = new Set(
    requirements
      .filter((row) => requirementMet(row) === true)
      .map((row) => cleanRequirementLabel(row.label).toLowerCase())
      .filter(Boolean),
  )
  if (metRequirementNames.size === 0) return []

  const inCatalog = catalogSkillNames(payload.candidate_skills_by_domain)
  return (payload.unmapped_skills ?? []).filter((skill) => {
    const fold = skill.display.trim().toLowerCase()
    if (!fold || inCatalog.has(fold)) return false
    return metRequirementNames.has(fold)
  })
}

/** V2 rubric criteria first; otherwise job required/preferred skills from the payload. */
export function resolveAnalysisRequirements(
  payload: EvaluationAnalysisPayload,
): {
  requirements: RequirementCoverageRow[]
  source: 'criteria' | 'legacy' | 'none'
} {
  const fromCriteria = groupCriteriaResults(payload.criteria_results).requirements
  if (fromCriteria.length > 0) {
    return {
      requirements: fromCriteria.map(toRequirementCoverageRow),
      source: 'criteria',
    }
  }

  const legacy = legacyRequirementsFromPayload(payload)
  if (legacy.length > 0) {
    return { requirements: legacy, source: 'legacy' }
  }

  return { requirements: [], source: 'none' }
}

function requirementStatusLabel(met: boolean | undefined): string {
  if (met === true) return 'Met'
  if (met === false) return 'Not evidenced'
  return 'Unknown'
}

function RequirementChecklistRows({ rows }: { rows: RequirementCoverageRow[] }) {
  return (
    <ul className="divide-y divide-border">
      {rows.map((criterion) => {
        const met = requirementMet(criterion)
        return (
          <li
            key={criterion.key}
            className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
          >
            <span className="flex min-w-0 items-center gap-2.5">
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                  met === true && 'bg-success/15 text-success',
                  met === false &&
                    'border border-dashed border-muted-foreground/50 text-muted-foreground',
                  met === undefined && 'text-muted-foreground',
                )}
                aria-hidden
              >
                {met === true ? '✓' : met === false ? '○' : '·'}
              </span>
              <span className="min-w-0 text-foreground">{criterion.label}</span>
            </span>
            <span
              className={cn(
                'shrink-0 text-xs font-medium',
                met === true && 'text-success',
                met === false && 'text-muted-foreground',
                met === undefined && 'text-muted-foreground',
              )}
            >
              {requirementStatusLabel(met)}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/** Must-have checklist — Required / Preferred groups, distinct from skills-by-domain chips. */
export function RequirementsCoverageSection({
  requirements,
  className,
  description = 'Checked against skills on this profile.',
}: RequirementsCoverageSectionProps) {
  if (!requirements.length) return null

  const rows = requirements.map(toRequirementCoverageRow)
  const requiredRows = rows.filter((row) => row.tier === 'required')
  const preferredRows = rows.filter((row) => row.tier === 'preferred')
  const { metCount, knownCount } = summarizeRequirements(rows)

  return (
    <section className={cn('space-y-3', className)}>
      <div className="space-y-1">
        <h3 className="text-sm font-semibold">Must-haves</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
        {knownCount > 0 ? (
          <p className="text-sm font-medium tabular-nums text-foreground">
            {metCount} of {knownCount} met
          </p>
        ) : null}
      </div>
      <div className="space-y-4">
        {requiredRows.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Required
            </p>
            <RequirementChecklistRows rows={requiredRows} />
          </div>
        ) : null}
        {preferredRows.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Preferred
            </p>
            <RequirementChecklistRows rows={preferredRows} />
          </div>
        ) : null}
      </div>
    </section>
  )
}
